import { useMutation, useQueryClient } from '@tanstack/react-query';

import { FeatureName } from '@ditto/shared';
import { useAuthenticatedClient, useProtectedQuery } from './useApi';

export const useWorkspaceFeaturesApi = (workspaceId: string) => {
  const { client } = useAuthenticatedClient(false);

  return useProtectedQuery({
    queryKey: ['workspaceFeatures', workspaceId],
    queryFn: async () => {
      const response = await client.workspaceFeature.getWorkspaceFeatures({
        params: { workspaceId },
      });

      if (response.status === 200) {
        return response.body;
      }

      // Extract error message from response body
      const errorMessage =
        (response.body as any)?.message ||
        `Request failed with status ${response.status}`;

      const error = new Error(errorMessage);
      (error as any).response = response;
      throw error;
    },
    enabled: !!workspaceId,
    retry: 1,
  });
};

export const useUpdateWorkspaceFeatureMutation = () => {
  const { client } = useAuthenticatedClient(false);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      workspaceId,
      features,
    }: {
      workspaceId: string;
      features: Array<{
        feature: FeatureName;
        status: 'ACTIVATED' | 'DEACTIVATED';
      }>;
    }) => {
      const response = await client.workspaceFeature.updateWorkspaceFeature({
        params: { workspaceId },
        body: { features },
      });

      if (response.status === 200) {
        return response.body;
      }

      // Extract error message from response body
      const errorMessage =
        (response.body as any)?.message ||
        `Request failed with status ${response.status}`;

      const error = new Error(errorMessage);
      (error as any).response = response;
      throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['workspaceFeatures', variables.workspaceId],
      });
      queryClient.invalidateQueries({
        queryKey: ['back-office-workspaces'],
      });
    },
  });
};
